import React, { useEffect, useMemo, useState } from "react";
import { fetchSiengePermissions, checkSiengePermissions } from "./siengePermissionsApi";

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "nunca verificado");

// Célula de um escopo: liberado / falta / desconhecido, com destaque do que mudou na
// última verificação (verde pulsante = liberado desde a última vez; vermelho = revogado).
const ScopeCell = ({ scope, changed }) => {
  if (!scope || scope.allowed === null) {
    const unknownTitle = scope?.status_code === 401
      ? "401 — credencial da empresa inválida: nenhuma rota pode ser verificada até corrigir o cadastro"
      : scope?.status_code == null && scope
        ? "Sem resposta do Sienge (rede/timeout) — verifique de novo"
        : "Não foi possível verificar (rede/limite) — verifique de novo";
    return <td className="scope-cell"><span className="scope-pill scope-unknown" title={unknownTitle}>?</span></td>;
  }
  const cls = scope.allowed ? "scope-yes" : "scope-no";
  const changeCls = changed === "granted" ? " scope-just-granted" : changed === "revoked" ? " scope-just-revoked" : "";
  return (
    <td className="scope-cell">
      <span className={`scope-pill ${cls}${changeCls}`} title={scope.usage}>
        {scope.allowed ? "liberado" : "falta"}
        {changed === "granted" && " ✓ novo"}
        {changed === "revoked" && " ⚠ revogado"}
      </span>
    </td>
  );
};

// A sondagem vem fatiada por grupo: o diff do primeiro grupo começa do zero, os seguintes
// acumulam, senão a linha perderia o "✓ novo" dos grupos já verificados neste clique.
const mergeChanges = (previous, incoming, reset) => {
  const base = reset || !previous ? { granted: [], revoked: [] } : previous;
  return {
    granted: [...new Set([...(base.granted ?? []), ...(incoming?.granted ?? [])])],
    revoked: [...new Set([...(base.revoked ?? []), ...(incoming?.revoked ?? [])])],
  };
};

// Recusas que valem para a construtora inteira, nao para o grupo sondado no momento.
const COMPANY_LEVEL_CODES = new Set(["sienge_credentials_missing"]);
const COMPANY_LEVEL_HINTS = {
  sienge_credentials_missing:
    "Cadastre subdomínio, usuário e senha da API no bloco Sienge do formulário da construtora.",
};

const ClientRow = ({ client, catalog, groups, onProbed }) => {
  // progress: null (parado) | {done, total, group} — a sondagem anda um grupo por requisição,
  // porque as 22 rotas do catálogo (bulk-data inclusive) não cabem no limite do gateway.
  const [progress, setProgress] = useState(null);
  const [failure, setFailure] = useState(null);
  const [credentialInvalid, setCredentialInvalid] = useState(false);
  const [row, setRow] = useState(client);
  const checking = progress !== null;

  const onCheck = async () => {
    setFailure(null);
    setCredentialInvalid(false);
    let current = row;
    let anyProbe = false;
    for (let index = 0; index < groups.length; index += 1) {
      const group = groups[index];
      setProgress({ done: index, total: groups.length, group });
      const res = await checkSiengePermissions(String(client._id), group);
      if (!res.ok || !res.data?.scopes) {
        // Uma recusa de nivel da construtora (credencial ausente, empresa fora do cadastro)
        // vale para as 22 rotas: atribui-la ao grupo que por acaso foi sondado primeiro faz o
        // operador procurar o problema em "Suprimentos" quando ele esta no cadastro.
        const companyLevel = COMPANY_LEVEL_CODES.has(res.data?.code);
        setProgress(null);
        setFailure({
          group: companyLevel ? null : group,
          message: res.message
            ?? "O servidor respondeu sem os dados da sondagem. Tente de novo.",
          hint: companyLevel ? COMPANY_LEVEL_HINTS[res.data.code] ?? null : null,
          partial: anyProbe && !companyLevel,
        });
        // O que já foi sondado neste clique sobe para a tabela mesmo com a falha adiante.
        if (anyProbe) onProbed?.(current);
        return;
      }
      anyProbe = true;
      if (res.data.credential_invalid) setCredentialInvalid(true);
      current = {
        ...current,
        scopes: res.data.scopes,
        denied: res.data.denied,
        last_change: mergeChanges(current.last_change, res.data.last_change, index === 0),
        probed_at: res.data.probed_at,
      };
      setRow(current);
      if (Array.isArray(res.data.pending) && res.data.pending.length > 0) {
        setFailure({
          group,
          message: `${res.data.pending.length} rota(s) de "${group}" não couberam no tempo da sondagem. Verifique de novo para completar.`,
          partial: true,
        });
      }
    }
    setProgress(null);
    onProbed?.(current);
  };

  const changeOf = (key) => {
    if (row.last_change?.granted?.includes(key)) return "granted";
    if (row.last_change?.revoked?.includes(key)) return "revoked";
    return null;
  };

  return (
    <tr>
      <td>
        <b>{row.name}</b>
        {row.subdomain ? <span className="sienge-perms-sub">{row.subdomain}</span> : null}
        {credentialInvalid && (
          <p className="sienge-perms-rowerror" role="status" style={{ color: "#b42318", margin: "4px 0 0" }}>
            Credencial do Sienge inválida (401). Nenhuma rota pode ser verificada até corrigir
            usuário/senha/subdomínio da empresa no cadastro.
          </p>
        )}
        {failure && (
          <p
            className="sienge-perms-rowerror"
            role="alert"
            style={{ color: failure.partial ? "#b54708" : "#b42318", margin: "4px 0 0" }}
          >
            {failure.group
              ? `${failure.partial ? "Verificação incompleta" : "Falha ao verificar"} em “${failure.group}”: ${failure.message}`
              : `Nenhuma rota pôde ser verificada: ${failure.message}`}
            {failure.hint ? ` ${failure.hint}` : ""}
          </p>
        )}
      </td>
      {catalog.map((col) => (
        <ScopeCell key={col.key} scope={row.scopes?.[col.key]} changed={changeOf(col.key)} />
      ))}
      <td className="sienge-perms-when">{fmtDateTime(row.probed_at)}</td>
      <td>
        <button type="button" disabled={checking} onClick={onCheck}>
          {checking
            ? `Verificando ${progress.group} (${progress.done + 1}/${progress.total})…`
            : row.probed_at ? "Reverificar" : "Verificar"}
        </button>
      </td>
    </tr>
  );
};

const SiengePermissions = () => {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);

  const load = async () => {
    setLoadError(null);
    setData(null);
    const res = await fetchSiengePermissions();
    if (!res.ok || !Array.isArray(res.data?.clients)) {
      setLoadError(res.message ?? "O servidor respondeu sem a lista de clientes.");
      return;
    }
    setData(res.data);
  };

  useEffect(() => { load(); }, []);

  const catalog = data?.catalog ?? [];
  const clients = data?.clients ?? [];
  // Ordem dos grupos = ordem do catálogo; é ela que a sondagem percorre.
  const groups = useMemo(
    () => [...new Set(catalog.map((col) => col.group || "Outros"))],
    [catalog],
  );

  const onProbed = (updated) => {
    setData((d) => ({
      ...d,
      clients: d.clients.map((c) => (String(c._id) === String(updated._id) ? updated : c)),
    }));
  };

  const visible = useMemo(
    () =>
      clients.filter((c) => {
        if (query && !c.name?.toLowerCase().includes(query.toLowerCase())) return false;
        if (onlyMissing && !(c.denied && c.denied.length > 0)) return false;
        return true;
      }),
    [clients, query, onlyMissing]
  );

  // Cobertura por API (quantos clientes já verificados têm cada escopo liberado),
  // agrupada pela categoria do painel do Sienge.
  const coverageGroups = useMemo(() => {
    const probed = clients.filter((c) => c.scopes);
    const byGroup = {};
    for (const col of catalog) {
      const g = col.group || "Outros";
      (byGroup[g] ??= []).push({
        ...col,
        allowed: probed.filter((c) => c.scopes?.[col.key]?.allowed === true).length,
        total: probed.length,
      });
    }
    return Object.entries(byGroup);
  }, [clients, catalog]);
  const anyProbed = clients.some((c) => c.scopes);

  return (
    <div className="sienge-perms-page integrations-page">
      <h2>Permissões da API Sienge</h2>
      <p className="margin-bottom-16">
        Ferramenta interna — o que a API do Sienge de cada cliente libera para o Ampli. Clique em
        <b> Verificar</b> para sondar ao vivo (o Sienge não expõe as permissões; sondamos com uma
        chamada mínima por API). A sondagem anda um grupo por vez, e o resultado fica registrado;
        ao reverificar, o que mudou desde a última vez aparece destacado. Um <b>?</b> significa
        que a rota não pôde ser verificada — não que ela esteja negada. As sondas são todas GET:
        a autorização de escrita (<code>PUT /bills/&#123;id&#125;/buildings-cost</code>) não é
        coberta aqui.
      </p>

      {anyProbed && (
        <div className="sienge-perms-coverage">
          {coverageGroups.map(([group, items]) => (
            <div key={group} className="sienge-perms-cov-group">
              <span className="sienge-perms-cov-grouplabel">{group}</span>
              <div className="sienge-perms-cov-items">
                {items.map((c) => (
                  <div key={c.key} className="sienge-perms-cov-item" title={c.usage}>
                    <span className="sienge-perms-cov-label">{c.label}</span>
                    <span className="sienge-perms-cov-val">{c.allowed}/{c.total}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="integrations-controls">
        <input
          type="search"
          placeholder="Filtrar por cliente…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label className="sienge-perms-onlymissing">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
          só com pendências
        </label>
      </div>

      {loadError ? (
        <div className="sienge-perms-error" role="alert">
          <p style={{ color: "#b42318" }}>{loadError}</p>
          <button type="button" onClick={load}>Tentar novamente</button>
        </div>
      ) : data === null ? (
        <p>Carregando…</p>
      ) : (
        <div className="integrations-scroll">
          <table className="integrations-table sienge-perms-table">
            <thead>
              <tr>
                <th>Cliente</th>
                {catalog.map((col) => (
                  <th key={col.key} className="scope-cell" title={col.usage}>{col.label}</th>
                ))}
                <th className="sienge-perms-when">Última verificação</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <ClientRow
                  key={String(c._id)}
                  client={c}
                  catalog={catalog}
                  groups={groups}
                  onProbed={onProbed}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SiengePermissions;
